<script>
import Vue from 'vue'
import { AgGridVue } from 'ag-grid-vue'

import AgreementUnionMxResource from '../../resources/agreementUnionMx'
import AgreementResource from '../../resources/agreement'
import UnionLocalResource from '../../resources/unionLocal'
import UnionJurisdictionResource from '../../resources/unionJurisdiction'

import toastMixin from '../../mixins/toast'
import GridAutocomplete from '../GridAutocomplete.vue'

export default Vue.extend({
    name: 'AgreementUnionMxGrid',
    mixins: [toastMixin],
    props: {
        rowData: {
            required: false,
            type: Array,
            default: () => {
                return []
            },
        },
    },
    components: {
        AgGridVue,
    },
    data() {
        return {
            agreementUnionMxColumnDefs: [
                {
                    field: 'id',
                    headerName: 'ID#',
                    minWidth: 40,
                    maxWidth: 70,
                    sortable: true,
                    editable: false,
                    valueSetter: () => null,
                    valueGetter: (params) => {
                        return params.data.id
                    },
                },
                {
                    field: 'agreement_id',
                    headerName: 'Agreement',
                    width: 100,
                    minWidth: 50,
                    maxWidth: 400,
                    sortable: true,
                    editable: false,
                    valueGetter: (params) => {
                        return params.data.get('agreement_id.name')
                    },
                    cellEditor: 'gridAutocomplete',
                    cellEditorParams: {
                        options: {
                            resourceClass: AgreementResource,
                            updateResource: true,
                        },
                    },
                },
                {
                    field: 'union_local_id',
                    headerName: 'Local',
                    width: 70,
                    minWidth: 40,
                    maxWidth: 120,
                    sortable: true,
                    editable: true,
                    valueGetter: (params) => {
                        return params.data.get('union_local_id.code')
                    },
                    cellEditor: 'gridAutocomplete',
                    cellEditorParams: {
                        options: {
                            resourceClass: UnionLocalResource,
                            updateResource: true,
                        },
                    },
                },
                {
                    field: 'jurisdiction_id',
                    headerName: 'Jurisdiction',
                    width: 70,
                    minWidth: 40,
                    maxWidth: 120,
                    sortable: true,
                    editable: true,
                    valueGetter: (params) => {
                        return params.data.get('jurisdiction_id.code')
                    },
                    cellEditor: 'gridAutocomplete',
                    cellEditorParams: {
                        options: {
                            resourceClass: UnionJurisdictionResource,
                            updateResource: true,
                            required: false,
                        },
                    },
                },
                {
                    field: 'status',
                    headerName: 'Active',
                    maxWidth: 80,
                    editable: true,
                    sortable: true,
                    cellRenderer: (params) => {
                        let input = document.createElement('input')
                        input.type = 'checkbox'
                        input.checked = params.data.get('status') > 0 ? true : false
                        input.addEventListener('change', (event) => {
                            params.data.set('status', event.target.checked ? 1 : 0)
                        })
                        return input
                    },
                    valueGetter: (params) => {
                        return params.data.get('status')
                    },
                },
                {
                    field: 'agreement_id_ro',
                    headerName: 'Ag ID',
                    minWidth: 60,
                    maxWidth: 80,
                    sortable: true,
                    valueSetter: () => {},
                    valueGetter: (params) => params.data.attributes.agreement_id,
                },
                {
                    field: 'union_local_id_ro',
                    headerName: 'Loc ID',
                    minWidth: 60,
                    maxWidth: 80,
                    sortable: true,
                    valueSetter: () => {},
                    valueGetter: (params) => params.data.attributes.union_local_id,
                },
                {
                    field: 'jurisdiction_id_ro',
                    headerName: 'Jurisdiction',
                    minWidth: 60,
                    maxWidth: 80,
                    sortable: true,
                    valueSetter: () => {},
                    valueGetter: (params) => params.data.attributes.jurisdiction_id,
                },
                {
                    field: 'agreement_propay_id_ro',
                    headerName: 'ProPay Agreement ID',
                    sortable: true,
                    valueGetter: (params) => params.data.attributes.agreement_propay_id,
                },
            ],
            agreementUnionMxRowData: [],
            agreementUnionMxResources: undefined,
            gridOptions: {
                enterMovesDown: true,
                enterMovesDownAfterEdit: true,
                defaultColDef: {
                    resizable: true,
                },
                frameworkComponents: {
                    gridAutocomplete: GridAutocomplete,
                },
                onGridReady: async (params) => {
                    if (params) {
                        this.gridApi = params.api
                        this.columnApi = params.columnApi
                    }
                    this.gridApi.redrawRows()
                },

                onFirstDataRendered: (params) => {
                    params.api.sizeColumnsToFit()
                },
            },
        }
    },
    computed: {
        resources() {
            return {
                AgreementUnionMxResource,
                AgreementResource,
                UnionLocalResource,
                UnionJurisdictionResource,
            }
        },
    },
    methods: {
        save() {
            let promises = []
            this.rowData.forEach((resource) => {
                if (Object.keys(resource.changes).length) {
                    let agreementUnionMxResourcePromise = resource
                        .save()
                        .then((result) => {
                            if (result.response.status > 200 || result.response.status < 299) {
                                this.createToast('b-toaster-top-center', 'success', true, 'User Updated', 'Agreement Union MX was updated')
                            }
                        })
                        .catch(() => {
                            this.createToast('b-toaster-top-center', 'danger', true, 'Update Error', 'The Agreement Union MX was not updated')
                        })
                    promises.push(agreementUnionMxResourcePromise)
                }
                return Promise.all(promises).then(() => undefined)
            })
        },
        exportGrid() {
            this.gridApi.exportDataAsExcel()
        },
    },
})
</script>

<template>
    <div>
        <h1 class="left">Agreement Union MX</h1>
        <div class="right">
            <b-button variant="outline-success" @click="exportGrid">Export</b-button>
            <b-button variant="primary" @click="save">Save</b-button>
        </div>
        <div class="clear"></div>
        <ag-grid-vue class="ag-theme-balham default-grid" :column-defs="agreementUnionMxColumnDefs" :row-data="rowData" :grid-options="gridOptions" />
        <div class="right">
            <b-button variant="outline-success" @click="exportGrid">Export</b-button>
            <b-button variant="primary" @click="save">Save</b-button>
        </div>
        <div class="clear"></div>
        <hr />
    </div>
</template>

<style>
@import '../../../node_modules/ag-grid-community/dist/styles/ag-grid.css';
@import '../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css';

.default-grid {
    width: 100%;
    height: 500px;
}
</style>
